import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type AdminLetterIn, type PaginatedResponse } from '../../lib/api';

export function InboundMailPage() {
  const [letters, setLetters] = useState<PaginatedResponse<AdminLetterIn> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const fetchLetters = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getLettersIn({
          page,
          status: statusFilter || undefined,
        });
        setLetters(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load letters');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLetters();
  }, [page, statusFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'bg-success/20 text-success';
      case 'rejected':
        return 'bg-ember/20 text-ember';
      case 'pending':
        return 'bg-amber-500/20 text-amber-400';
      case 'error':
        return 'bg-ember/20 text-ember';
      default:
        return 'bg-stone/30 text-mist';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-parchment">Inbound Mail</h1>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="processed">Processed</option>
          <option value="rejected">Rejected</option>
          <option value="error">Error</option>
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="text-mist animate-pulse">Loading inbound mail...</div>
        </div>
      )}

      {/* Letters Table */}
      {!isLoading && letters && (
        <>
          <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                  <th className="px-4 py-3 font-medium">Received</th>
                  <th className="px-4 py-3 font-medium">From</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">World</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {letters.data.map((letter) => (
                  <tr
                    key={letter.id}
                    className="border-b border-stone/20 last:border-0 hover:bg-stone/10"
                  >
                    <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">
                      {formatDate(letter.receivedAt)}
                    </td>
                    <td className="px-4 py-3 text-parchment">{letter.fromEmail}</td>
                    <td className="px-4 py-3 text-parchment truncate max-w-xs">
                      {letter.subject ?? <span className="text-mist/50 italic">No subject</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/worlds/${letter.worldId}`}
                        className="text-rune hover:underline text-xs font-mono"
                      >
                        {letter.worldAddress.split('@')[0]}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getStatusColor(letter.status)}`}
                      >
                        {letter.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/letters-in/${letter.id}`}
                        className="text-rune hover:underline text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {letters.data.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-mist">
                      No inbound letters found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {letters.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-mist">{letters.pagination.total} total letters</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-mist">
                  Page {page} of {letters.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === letters.pagination.totalPages}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}
